<template>
  <div class="cinema-stage">
    <!-- 顶部标题与信息胶囊栏 -->
    <div class="stage-header">
      <div class="header-left">
        <div class="title-capsule" :title="previewData.name || '素材预览'">
          <v-icon small color="#10b981" class="mr-1">mdi-movie-open-outline</v-icon>
          <span class="title-text">{{ previewData.name || '素材预览' }}</span>
        </div>
      </div>

      <div class="header-right">
        <!-- 专业级键盘快捷键提示胶囊 -->
        <div class="shortcut-pill" title="支持键盘方向键 ← / → 或 A / D 极速切图，Esc 关闭">
          <span class="key-cap">←</span>
          <span class="key-cap">→</span>
          <span class="shortcut-text">切图</span>
        </div>
        <!-- 序列进度指示器，如 12 / 85 -->
        <span class="counter-badge" v-if="total > 0">
          {{ current + 1 }} / {{ total }}
        </span>
      </div>
    </div>

    <!-- 中央大舞台沉浸视口 -->
    <div class="stage-viewport">
      <!-- 上一张切换按钮（吸附在画面左侧） -->
      <button
        class="stage-nav-btn prev-btn"
        @click="$emit('ccPrev')"
        aria-label="上一张素材"
        title="上一张 (←)"
      >
        <v-icon medium>mdi-chevron-left</v-icon>
      </button>

      <!-- 居中素材屏幕外壳：智能高宽比自适应，杜绝方框锁死 -->
      <div class="stage-screen-wrap">
        <div
          class="media-container"
          :class="{ 'is-portrait': isPortrait, 'is-landscape': !isPortrait }"
          :style="mediaContainerStyle"
        >
          <iframe
            class="gospel-imgs"
            ref="gospelImgs"
            :src="previewData.preview_url"
            frameborder="0"
            allowfullscreen
          ></iframe>
        </div>
      </div>

      <!-- 下一张切换按钮（吸附在画面右侧） -->
      <button
        class="stage-nav-btn next-btn"
        @click="$emit('ccNext')"
        aria-label="下一张素材"
        title="下一张 (→)"
      >
        <v-icon medium>mdi-chevron-right</v-icon>
      </button>
    </div>

    <!-- 舞台底部辅助栏 -->
    <div class="stage-footer">
      <button
        class="toggle-sidebar-btn"
        :class="{ 'active': isShowInfo }"
        @click="$emit('ccToggleInfo')"
        :title="isShowInfo ? '收起详情信息' : '展开详情信息'"
      >
        <v-icon x-small class="mr-1">{{ isShowInfo ? 'mdi-chevron-right' : 'mdi-chevron-left' }}</v-icon>
        <span>{{ isShowInfo ? '收起详情' : '展开详情' }}</span>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "image-preview",
  props: {
    previewData: {
      type: Object,
      default: () => ({}),
    },
    current: {
      type: Number,
      default: 0,
    },
    total: {
      type: Number,
      default: 0,
    },
    isShowInfo: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    isPortrait() {
      const w = parseFloat(this.previewData.width);
      const h = parseFloat(this.previewData.height);
      return Boolean(w > 0 && h > 0 && h > w);
    },
    mediaContainerStyle() {
      const w = parseFloat(this.previewData.width);
      const h = parseFloat(this.previewData.height);
      if (w > 0 && h > 0) {
        return {
          aspectRatio: `${w} / ${h}`,
        };
      }
      return {
        aspectRatio: "16 / 9",
      };
    },
  },
  mounted() {
    this._keyHandler = (e) => {
      switch (e.keyCode) {
        case 37: // 左箭头
          this.$emit("ccPrev");
          break;
        case 39: // 右箭头
          this.$emit("ccNext");
          break;
        default:
          break;
      }
    };
    document.addEventListener("keydown", this._keyHandler);
  },
  beforeDestroy() {
    if (this._keyHandler) {
      document.removeEventListener("keydown", this._keyHandler);
    }
  },
};
</script>

<style lang="scss">
.cinema-stage {
  flex: 1 1 0;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: rgba(9, 14, 26, 0.65);
  position: relative;
  overflow: hidden;

  // 1. 顶部标题胶囊栏
  .stage-header {
    flex: 0 0 56px;
    height: 56px;
    padding: 0 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    background: rgba(15, 23, 42, 0.4);

    .header-left {
      flex: 1;
      min-width: 0;
      margin-right: 16px;

      .title-capsule {
        display: inline-flex;
        align-items: center;
        max-width: 100%;
        padding: 5px 14px;
        border-radius: 20px;
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.08);

        .title-text {
          font-family: "Noto Sans SC", -apple-system, BlinkMacSystemFont, sans-serif;
          font-size: 14px;
          font-weight: 600;
          color: #f1f5f9;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          letter-spacing: 0.02em;
        }
      }
    }

    .header-right {
      display: flex;
      align-items: center;

      .shortcut-pill {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 3px 8px;
        margin-right: 12px;
        border-radius: 8px;
        background: rgba(255, 255, 255, 0.04);
        border: 1px solid rgba(255, 255, 255, 0.08);

        .key-cap {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 18px;
          height: 18px;
          border-radius: 4px;
          background: rgba(255, 255, 255, 0.1);
          color: #94a3b8;
          font-size: 11px;
          font-weight: 700;
          line-height: 1;
        }

        .shortcut-text {
          font-size: 11px;
          color: #64748b;
          font-weight: 500;
          margin-left: 2px;
        }
      }

      .counter-badge {
        padding: 4px 10px;
        font-size: 12px;
        font-weight: 600;
        color: #94a3b8;
        background: rgba(255, 255, 255, 0.05);
        border-radius: 12px;
        border: 1px solid rgba(255, 255, 255, 0.08);
        letter-spacing: 0.05em;
      }
    }
  }

  // 2. 中央大舞台视口
  .stage-viewport {
    flex: 1 1 0;
    min-height: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    position: relative;
    box-sizing: border-box;

    // 左右紧凑翻页控制器
    .stage-nav-btn {
      flex: 0 0 46px;
      width: 46px;
      height: 46px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.06);
      border: 1px solid rgba(255, 255, 255, 0.12);
      backdrop-filter: blur(14px);
      color: #e2e8f0;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.22s cubic-bezier(0.16, 1, 0.3, 1);
      outline: none;
      z-index: 10;

      &:hover {
        background: rgba(16, 185, 129, 0.22);
        border-color: rgba(52, 211, 153, 0.5);
        color: #34d399;
        transform: scale(1.1);
        box-shadow: 0 6px 20px -2px rgba(16, 185, 129, 0.4);
      }

      &:active {
        transform: scale(0.95);
      }

      &.prev-btn {
        margin-right: 16px;
      }

      &.next-btn {
        margin-left: 16px;
      }
    }

    // 画面屏幕外框
    .stage-screen-wrap {
      flex: 1 1 0;
      height: 100%;
      min-width: 0;
      min-height: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;

      .media-container {
        position: relative;
        max-width: 100%;
        max-height: 100%;
        border-radius: 14px;
        overflow: hidden;
        background: #000000;
        box-shadow: 0 24px 64px -12px rgba(0, 0, 0, 0.85), 0 0 0 1px rgba(255, 255, 255, 0.1);
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.25s ease;

        &.is-portrait {
          height: 100%;
          width: auto;
        }

        &.is-landscape {
          width: 100%;
          height: auto;
        }

        .gospel-imgs {
          width: 100%;
          height: 100%;
          border: none;
          display: block;
          background: #000000;
        }
      }
    }
  }

  // 3. 舞台底部辅助栏
  .stage-footer {
    flex: 0 0 40px;
    height: 40px;
    padding: 0 24px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    border-top: 1px solid rgba(255, 255, 255, 0.05);
    background: rgba(15, 23, 42, 0.3);

    .toggle-sidebar-btn {
      display: inline-flex;
      align-items: center;
      padding: 4px 10px;
      border-radius: 6px;
      font-size: 11px;
      color: #94a3b8;
      background: rgba(255, 255, 255, 0.04);
      border: 1px solid rgba(255, 255, 255, 0.08);
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        color: #ffffff;
        background: rgba(255, 255, 255, 0.08);
        border-color: rgba(255, 255, 255, 0.16);
      }
    }
  }
}
</style>